import React, { useEffect, useMemo, useRef, useState } from 'react'
import { hapticTick } from '../../../../../../_utils/haptics'
import { POST_TYPES, TYPE_ORDER, restyle, changeType, designName, designsFor, layoutOf } from '../../data/postTypes'
import { FAMILIES } from '../../render/families'
import { postTheme } from '../../render/render'
import { isSeasonal, seasonalOf, seasonalLayouts, seasonsFrom, withSalon } from '../../kits'
import LazyPreview from './LazyPreview'
import { useSeasonJump } from './useSeasonJump'

// A design picked here wears its own colours and fonts: the salon's colour
// and font changes belong to the design they were made on
export const lookOnly = (tune, familyChanged) => {
  const next = { ...(tune || {}) }
  delete next.colours
  if (familyChanged) { delete next.font; delete next.textFont }
  return next
}

// The post in another design, of its own kind or another: another kind
// keeps the look and starts from that kind's words (Undo brings these
// back); a season's design takes the salon's name
export const designOption = (post, { type, family, layout }, { salonName = '' } = {}) => {
  const base = type === post.type ? post : changeType(post, type)
  const same = base.family === family && (base.layout || '') === (layout || '')
  const next = same ? base : restyle(base, family, undefined, layout)
  return {
    ...next,
    ...(isSeasonal(layout) ? { content: withSalon(next.content, salonName) } : {}),
    tune: lookOnly(post.tune, family !== post.family),
  }
}

const isOn = (post, o) => o.type === post.type && o.family === post.family && (o.layout || '') === (post.layout || '')
const kindGroup = (type, post) => ({
  key: type,
  title: type === post.type ? `${POST_TYPES[type].label} designs` : POST_TYPES[type].label,
  sub: POST_TYPES[type].blurb,
  options: designsFor(type).map(d => ({ type, ...d })),
})
const seasonGroup = season => ({
  key: `season-${season.id}`,
  title: season.name,
  sub: 'Seasonal designs, drawn for the time of year',
  options: seasonalLayouts(season.id).map(layout => ({ type: 'announce', family: 'layouts', layout })),
  seasonId: season.id,
})

// A group of designs under a heading
const Group = ({ group, post, kit, format, salonName, onPick, more = null }) => (
  <section className="sx-designs-group" data-season={group.seasonId} aria-label={`${group.title} designs`}>
    <header className="sx-designs-head">
      <strong>{group.title}</strong>
      <span>{group.sub}.</span>
      {more}
    </header>
    <div className="sx-choice-grid">
      {group.options.map((o) => {
        const option = designOption(post, o, { salonName })
        const on = isOn(post, o)
        const l = o.layout && layoutOf(o.type, o.layout)
        return (
          <button key={`${o.type}|${o.family}|${o.layout || ''}`} type="button" className={`sx-choice${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => { if (on) return; hapticTick(); onPick(option) }}>
            <LazyPreview post={option} theme={postTheme(option, { kit })} format={format} />
            <span className="sx-choice-name">{designName(o.type, o.family, o.layout)}</span>
            <span className="sx-choice-sub">{l ? (l.purpose || l.blurb) : FAMILIES[o.family].name}</span>
          </button>
        )
      })}
    </div>
  </section>
)

// Every season's designs in one scroll, the next ones first, under chips
// that jump to a season and follow the scroll; opens at `start`
const SeasonalDesigns = ({ seasons, start, ...rest }) => {
  const { barRef, active, jump } = useSeasonJump(seasons[0] && seasons[0].id)
  useEffect(() => { if (start) jump(start) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className="sx-designs-filters is-seasons" ref={barRef} role="navigation" aria-label="Go to a season">
        {seasons.map(x => (
          <button key={x.id} type="button" data-chip={x.id} aria-current={x.id === active ? 'true' : undefined} className={`sx-chip${x.id === active ? ' is-on' : ''}`} onClick={() => { hapticTick(); jump(x.id) }}>{x.name}</button>
        ))}
      </div>
      {seasons.map(x => <Group key={x.id} group={seasonGroup(x)} {...rest} />)}
    </>
  )
}

// Every design, each once (colours are the Colours tool's), with filters:
// All (this post's kind, the season on now or next, then every other
// kind), Seasonal (every season, the next ones first) or one kind of post
const DesignsPanel = ({ post, kit, format, onPick }) => {
  const seasons = useMemo(() => seasonsFrom(), [])
  const here = seasonalOf(post.layout)
  const [filter, setFilter] = useState('all')
  const [start, setStart] = useState(here ? here.season.id : null)
  const topRef = useRef(null)
  const kinds = [post.type, ...TYPE_ORDER.filter(t => t !== post.type)]
  const filters = [{ id: 'all', label: 'All' }, { id: 'seasonal', label: 'Seasonal' }, ...kinds.map(t => ({ id: t, label: POST_TYPES[t].label }))]
  const nextSeason = here ? here.season : seasons[0]
  const groups = filter === 'all'
    ? [...(here ? [seasonGroup(here.season)] : []), kindGroup(post.type, post), ...(!here && nextSeason ? [seasonGroup(nextSeason)] : []), ...kinds.filter(t => t !== post.type).map(t => kindGroup(t, post))]
    : [kindGroup(filter, post)]

  const choose = (id) => {
    if (id === filter) return
    hapticTick()
    setFilter(id)
    if (topRef.current) topRef.current.scrollIntoView({ block: 'nearest' })
  }
  const shared = { post, kit, format, salonName: kit.name || '', onPick }

  return (
    <div className="sx-designs">
      <div className="sx-designs-filters" ref={topRef} role="radiogroup" aria-label="Show designs">
        {filters.map(f => (
          <button key={f.id} type="button" role="radio" aria-checked={filter === f.id} className={`sx-chip${filter === f.id ? ' is-on' : ''}`} onClick={() => choose(f.id)}>{f.label}</button>
        ))}
      </div>
      {filter === 'seasonal'
        ? <SeasonalDesigns seasons={seasons} start={start} {...shared} />
        : groups.map(g => (
          <Group
            key={g.key}
            group={g}
            {...shared}
            more={g.seasonId ? <button type="button" className="sx-link sx-designs-more" onClick={() => { setStart(g.seasonId); choose('seasonal') }}>See every season</button> : null}
          />
        ))}
    </div>
  )
}

export default DesignsPanel
